<script setup>
import SearchInput from "@/v2/components/Search/shared/SearchInput.vue";
import SearchLayout from "@/v2/components/Search/shared/SearchLayout.vue";
import SearchPopularSearches from "@/v2/components/Search/shared/SearchPopularSearches.vue";
import SearchResultItems from "@/v2/components/Search/shared/SearchResultItems.vue";
import SearchEmptyResults from "@/v2/components/Search/shared/SearchEmptyResults.vue";
import SearchResultThumbnail from "@/v2/components/Search/shared/SearchResultThumbnail.vue";
import Keyboard from "@/v2/components/Keyboard/Keyboard.vue";
import {
  SEARCH_IDLE_TIMEOUT,
  SEARCH_IDLE_TIMEOUT_TOUCHSCREEN,
} from "@/util/constants.ts";
import { ref, computed, nextTick, watch, onMounted } from "vue";

const query = defineModel("query", { required: true, type: String });

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  popularOptions: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  showKeyboard: {
    type: Boolean,
    required: false,
    default: true,
  },
  touchscreenMode: {
    type: Boolean,
    required: false,
    default: true,
  },
  visitedItems: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(["selected", "queryChanged", "idle", "close"]);

const searchLayout = ref(null);

const highlightedOptionPath = ref("");
const highlightedOptionCaption = ref("");
const isThumbnailVisible = ref(false);

const popularOptions = computed(() => props.popularOptions ?? []);
const filteredOptions = computed(() => props.options ?? []);

const idleTimeout = ref(null);

const thumbnailImageUrl = computed(
  () =>
    filteredOptions.value.find(
      (option) => option.path === highlightedOptionPath.value
    )?.thumbnail
);

const showEmptyResults = computed(
  () =>
    query.value &&
    query.value.length > 3 &&
    !filteredOptions.value.length &&
    !props.isLoading
);

function handleHighlightOption(option) {
  highlightedOptionPath.value = option.path;
  highlightedOptionCaption.value = option.name;

  nextTick(() => {
    isThumbnailVisible.value = Boolean(option.thumbnail);
  });
}

function resetIdleTimeout() {
  clearTimeout(idleTimeout.value);

  idleTimeout.value = setTimeout(
    () => {
      emit("idle");
    },
    props.touchscreenMode
      ? SEARCH_IDLE_TIMEOUT_TOUCHSCREEN
      : SEARCH_IDLE_TIMEOUT
  );
}

function handleQueryChange(updatedQuery) {
  searchLayout.value.scrollToTop();

  query.value = updatedQuery;

  isThumbnailVisible.value = false;

  resetIdleTimeout();

  emit("queryChanged", updatedQuery);
}

watch(
  () => filteredOptions.value,
  () => {
    if (query.value && filteredOptions.value[0]) {
      handleHighlightOption(filteredOptions.value[0]);
    }
  },
  {
    deep: true,
  }
);

function handleSelectOption(optionToSelect) {
  emit("selected", optionToSelect);
}

function handleVirtualKeyboardKeyPress(key) {
  switch (key) {
    case "{shift}":
      break;
    case "{space}":
      if (!query.value) {
        return;
      }

      handleQueryChange(`${query.value} `);
      break;
    case "{bksp}":
      handleQueryChange(query.value.substring(0, query.value.length - 1));
      break;
    default:
      handleQueryChange(`${query.value}${key}`);
      break;
  }
}

function clear() {
  handleQueryChange("");
}

defineExpose({
  clear,
});

onMounted(resetIdleTimeout);
</script>

<template>
  <SearchLayout
    ref="searchLayout"
    :show-keyboard="showKeyboard"
    data-testid="search-layout"
    @mouseover="resetIdleTimeout"
    @mousewheel="resetIdleTimeout"
    @mousedown="resetIdleTimeout"
    @close="$emit('idle')"
  >
    <template #input>
      <SearchInput
        :query="query"
        :readonly="showKeyboard"
        @input="handleQueryChange"
      />
    </template>

    <template #results>
      <SearchPopularSearches
        v-if="!query"
        :options="popularOptions"
        :visited-items
        @on-select-option="handleSelectOption"
      />

      <SearchResultItems
        v-else
        :options="filteredOptions"
        :highlighted-option-path="highlightedOptionPath"
        :visited-items
        @on-select-option="handleSelectOption"
      />
    </template>

    <template v-if="showEmptyResults" #empty-results>
      <SearchEmptyResults @start-over="clear" />
    </template>

    <template #thumbnail>
      <SearchResultThumbnail
        v-if="isThumbnailVisible"
        :image-url="thumbnailImageUrl"
        :caption="highlightedOptionCaption"
      />
    </template>

    <template #keyboard>
      <Keyboard data-theme="Dark" @key-press="handleVirtualKeyboardKeyPress" />
    </template>
  </SearchLayout>
</template>
